import { type PublicPortfolioAppMetrics, normalizePublicBundleId } from "@point/core";

import { useMemo, useSyncExternalStore } from "react";

type PortfolioStatsSnapshot = {
	parsed: {
		byBundleId: Map<string, PublicPortfolioAppMetrics>;
		summary: {
			mrr: number;
			arr: number;
			grossMrr?: number;
			totalActiveSubscribers: number;
			totalFreeTrialers?: number;
			totalApps: number;
			liveApps: number;
		} | null;
	} | null;
	error: boolean;
	loaded: boolean;
};

const PORTFOLIO_STATS_URL = (import.meta.env.VITE_PORTFOLIO_STATS_URL as string | undefined)?.trim();

const EMPTY_PORTFOLIO_METRICS = new Map<string, PublicPortfolioAppMetrics>();

function parsePortfolioJson(raw: unknown): NonNullable<PortfolioStatsSnapshot["parsed"]> {
	const byBundleId = new Map<string, PublicPortfolioAppMetrics>();
	let summary: NonNullable<PortfolioStatsSnapshot["parsed"]>["summary"] = null;

	if (!raw || typeof raw !== "object") return { byBundleId, summary: null };
	const o = raw as Record<string, unknown>;

	if (o.summary && typeof o.summary === "object") {
		const s = o.summary as Record<string, unknown>;
		summary = {
			mrr: typeof s.mrr === "number" ? s.mrr : 0,
			arr: typeof s.arr === "number" ? s.arr : 0,
			grossMrr: typeof s.grossMrr === "number" ? s.grossMrr : undefined,
			totalActiveSubscribers: typeof s.totalActiveSubscribers === "number" ? s.totalActiveSubscribers : 0,
			totalFreeTrialers: typeof s.totalFreeTrialers === "number" ? s.totalFreeTrialers : undefined,
			totalApps: typeof s.totalApps === "number" ? s.totalApps : 0,
			liveApps: typeof s.liveApps === "number" ? s.liveApps : 0,
		};
	}

	const apps = o.apps;
	if (!Array.isArray(apps)) return { byBundleId, summary };

	for (const item of apps) {
		if (!item || typeof item !== "object") continue;
		const a = item as Record<string, unknown>;
		const bid = normalizePublicBundleId(typeof a.bundleId === "string" ? a.bundleId : "");
		if (!bid) continue;
		byBundleId.set(bid, {
			bundleId: typeof a.bundleId === "string" ? a.bundleId : bid,
			mrr: typeof a.mrr === "number" ? a.mrr : undefined,
			revenue30d: typeof a.revenue30d === "number" ? a.revenue30d : undefined,
			downloads30d: typeof a.downloads30d === "number" ? a.downloads30d : undefined,
			activeSubscribers: typeof a.activeSubscribers === "number" ? a.activeSubscribers : undefined,
			freeTrialers: typeof a.freeTrialers === "number" ? a.freeTrialers : undefined,
			rating: typeof a.rating === "number" ? a.rating : undefined,
			ratingCount: typeof a.ratingCount === "number" ? a.ratingCount : undefined,
		});
	}

	return { byBundleId, summary };
}

/**
 * Optional merge of App Store portfolio metrics (MRR, revenue, etc.).
 * Set `VITE_PORTFOLIO_STATS_URL` to your `/api/appstore/portfolio-stats` URL
 * (must send CORS for this site’s origin — see main server `portfolio-stats`).
 */
function createPortfolioStatsStore(url: string | undefined) {
	let snapshot: PortfolioStatsSnapshot = {
		parsed: null,
		error: false,
		loaded: !url,
	};
	const listeners = new Set<() => void>();

	const emit = () => {
		for (const listener of listeners) listener();
	};

	const setSnapshot = (nextSnapshot: PortfolioStatsSnapshot) => {
		snapshot = nextSnapshot;
		emit();
	};

	if (url) {
		void (async () => {
			try {
				const res = await fetch(url, {
					method: "GET",
					credentials: "omit",
					cache: "no-store",
					headers: { Accept: "application/json" },
				});
				if (!res.ok) throw new Error(String(res.status));
				const json: unknown = await res.json();
				setSnapshot({
					parsed: parsePortfolioJson(json),
					error: false,
					loaded: true,
				});
			} catch {
				setSnapshot({
					parsed: null,
					error: true,
					loaded: true,
				});
			}
		})();
	}

	return {
		getSnapshot: () => snapshot,
		subscribe: (listener: () => void) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}

const portfolioStatsStore = createPortfolioStatsStore(PORTFOLIO_STATS_URL);

export function usePortfolioStats() {
	const snapshot = useSyncExternalStore(portfolioStatsStore.subscribe, portfolioStatsStore.getSnapshot, portfolioStatsStore.getSnapshot);

	return useMemo(
		() => ({
			enabled: Boolean(PORTFOLIO_STATS_URL),
			byBundleId: snapshot.parsed?.byBundleId ?? EMPTY_PORTFOLIO_METRICS,
			summary: snapshot.parsed?.summary ?? null,
			loaded: snapshot.loaded,
			error: snapshot.error,
		}),
		[snapshot],
	);
}

export function lookupPortfolioMetrics(map: Map<string, PublicPortfolioAppMetrics>, bundleId: string | undefined): PublicPortfolioAppMetrics | null {
	const k = normalizePublicBundleId(bundleId);
	if (!k) return null;
	return map.get(k) ?? null;
}
